// src/services/phantom-deeplink.service.ts
import {  
  Connection, 
  Transaction, 
  PublicKey,
  SystemProgram,
  LAMPORTS_PER_SOL
} from '@solana/web3.js';
import bs58 from 'bs58';
import nacl from 'tweetnacl';

class PhantomDeeplinkService {
  private sharedSecret: Uint8Array | null = null;
  private session: string | null = null;
  private encryptionKeys: { secretKey: Uint8Array; publicKey: Uint8Array } | null = null;

  // Network configuration - ensure this matches your Phantom wallet setting
  private readonly NETWORK = 'mainnet-beta' as const;
  
  private connection = new Connection(
    process.env.REACT_APP_SOLANA_RPC_ENDPOINT || 'https://mainnet.helius-rpc.com/?api-key=37327d2d-9742-4fc4-b0d0-57d489b78697',
    'confirmed'
  );

  constructor() {
    this.initializeEncryptionKeys();
    this.verifySetup();
  }

  private async verifySetup() {
    console.log('🔍 Phantom Service Setup:');
    console.log('  Network:', this.NETWORK);
    console.log('  RPC:', process.env.REACT_APP_SOLANA_RPC_ENDPOINT ? 'Helius' : 'Default');
    
    try {
      const version = await this.connection.getVersion();
      console.log('  RPC Connected:', version ? '✅' : '❌');
      
      // Check if wallet is connected
      const walletKey = localStorage.getItem('phantom_public_key');
      if (walletKey) {
        const balance = await this.connection.getBalance(new PublicKey(walletKey));
        console.log('  Wallet:', walletKey.substring(0, 8) + '...');
        console.log('  Balance:', (balance / LAMPORTS_PER_SOL).toFixed(4), 'SOL');
        
        if (balance < 0.001 * LAMPORTS_PER_SOL) {
          console.warn('⚠️ Low balance! Need at least 0.001 SOL for fees');
        }
      }
    } catch (e) {
      console.error('Setup verification failed:', e);
    }
  }

  private initializeEncryptionKeys() {
    const savedKeyPair = localStorage.getItem('dapp_encryption_keypair');
    if (savedKeyPair) {
      try {
        const keyData = JSON.parse(savedKeyPair);
        const secretKey = new Uint8Array(keyData.secretKey);
        const publicKey = new Uint8Array(keyData.publicKey);
        
        if (secretKey.length === 32 && publicKey.length === 32) {
          this.encryptionKeys = { secretKey, publicKey };
          console.log('Restored encryption keypair');
        } else {
          throw new Error('Invalid key size');
        }
      } catch (error) {
        console.error('Failed to restore keypair:', error);
        this.generateNewEncryptionKeys();
      }
    } else {
      this.generateNewEncryptionKeys();
    }
    
    if (this.encryptionKeys) {
      localStorage.setItem('phantom_dapp_public_key', bs58.encode(this.encryptionKeys.publicKey));
    }
  }

  private generateNewEncryptionKeys() {
    const keyPair = nacl.box.keyPair();
    this.encryptionKeys = {
      secretKey: keyPair.secretKey,
      publicKey: keyPair.publicKey
    };
    
    localStorage.setItem('dapp_encryption_keypair', JSON.stringify({
      secretKey: Array.from(keyPair.secretKey),
      publicKey: Array.from(keyPair.publicKey)
    }));
    
    localStorage.setItem('phantom_dapp_public_key', bs58.encode(keyPair.publicKey));
    console.log('Generated new encryption keypair');
  }

  private restoreSharedSecret(): boolean {
    try {
      const storedSecretArray = localStorage.getItem('phantom_shared_secret');
      const phantomEncryptionKey = localStorage.getItem('phantom_encryption_public_key');
      
      if (storedSecretArray && phantomEncryptionKey && this.encryptionKeys) {
        const secretArray = JSON.parse(storedSecretArray);
        this.sharedSecret = new Uint8Array(secretArray);
        console.log('Restored shared secret from localStorage');
        return true;
      }
      
      if (phantomEncryptionKey && this.encryptionKeys) {
        const phantomPubKeyBytes = bs58.decode(phantomEncryptionKey);
        this.sharedSecret = nacl.box.before(
          phantomPubKeyBytes,
          this.encryptionKeys.secretKey
        );
        
        localStorage.setItem('phantom_shared_secret', JSON.stringify(Array.from(this.sharedSecret)));
        console.log('Recreated shared secret');
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Failed to restore shared secret:', error);
      return false;
    }
  }

  private isMobile(): boolean {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  private isInPhantomBrowser(): boolean {
    return !!(window as any).solana?.isPhantom && this.isMobile();
  }

  async connect(): Promise<string> {
    if (this.isMobile() && !this.isInPhantomBrowser()) {
      return this.connectViaDeeplink();
    } else {
      return this.connectDirect();
    }
  }

  private async connectViaDeeplink(): Promise<string> {
  const currentOrigin = window.location.origin;

  if (!this.encryptionKeys) {
    throw new Error('Encryption keys not initialized');
  }

  const redirectUrl = `${currentOrigin}/?phantom_redirect=true`;

  const params = new URLSearchParams({
    dapp_encryption_public_key: bs58.encode(this.encryptionKeys.publicKey),
    cluster: this.NETWORK,
    app_url: currentOrigin,
    redirect_link: redirectUrl
  });

  const ul = `https://phantom.app/ul/v1/connect?${params.toString()}`;
  const custom = `phantom://v1/connect?${params.toString()}`;

  localStorage.setItem('phantom_connection_attempt', Date.now().toString());
  localStorage.setItem('phantom_expected_origin', currentOrigin);
  localStorage.setItem('phantom_network', this.NETWORK);

  console.log('Connecting to Phantom app on', this.NETWORK);

  window.location.href = ul;
  setTimeout(() => {
    try { window.location.href = custom; } catch {}
  }, 350);

  throw new Error('Redirecting to Phantom app...');
}


  private async connectDirect(): Promise<string> {
    if (!window.solana?.isPhantom) {
      throw new Error('Phantom wallet not found');
    }

    const provider = window.solana;
    const resp = await provider.connect();
    return resp.publicKey.toString();
  }

  handlePhantomRedirect(): Promise<string> {
    return new Promise((resolve, reject) => {
      const urlParams = new URLSearchParams(window.location.search);
      const phantomEncryptionPublicKey = urlParams.get('phantom_encryption_public_key');
      const data = urlParams.get('data');
      const nonce = urlParams.get('nonce');

      if (!phantomEncryptionPublicKey || !data || !nonce) {
        reject(new Error('Missing connection parameters'));
        return;
      }

      try {
        if (!this.encryptionKeys) {
          throw new Error('Encryption keys not initialized');
        }

        const phantomPubKeyBytes = bs58.decode(phantomEncryptionPublicKey);
        this.sharedSecret = nacl.box.before(
          phantomPubKeyBytes,
          this.encryptionKeys.secretKey
        );

        const connectionData = this.decryptPayload(data, nonce, this.sharedSecret);
        
        const publicKey = connectionData.public_key || connectionData.publicKey;
        if (!publicKey) {
          throw new Error('No public key in connection data');
        }
        
        this.session = connectionData.session;
        if (this.session) {
          localStorage.setItem('phantom_session', this.session);
        }
        
        localStorage.setItem('phantom_shared_secret', JSON.stringify(Array.from(this.sharedSecret)));
        localStorage.setItem('phantom_public_key', publicKey);
        localStorage.setItem('phantom_connected_at', Date.now().toString());
        localStorage.setItem('phantom_encryption_public_key', phantomEncryptionPublicKey);

        localStorage.removeItem('phantom_connection_attempt');
        localStorage.removeItem('phantom_expected_origin');

        console.log('Phantom connection successful:', publicKey);
        resolve(publicKey);
      } catch (error) {
        console.error('Error handling redirect:', error);
        reject(error);
      }
    });
  }

  async signTransaction(transaction: Transaction): Promise<string> {
  console.log('📝 Starting transaction signing...');

  if (!this.hasValidSession()) {
    throw new Error('No valid session. Please connect your wallet first.');
  }

  if (!this.sharedSecret) {
    console.log('Restoring shared secret...');
    if (!this.restoreSharedSecret()) {
      throw new Error('Session expired. Please reconnect your wallet.');
    }
  }

  const session = localStorage.getItem('phantom_session');
  const dappPublicKey = localStorage.getItem('phantom_dapp_public_key');
  const walletPublicKey = localStorage.getItem('phantom_public_key');

  if (!session || !dappPublicKey || !walletPublicKey) {
    throw new Error('Missing session data. Please reconnect.');
  }

  try {
    // Wallet balance check (best effort)
    try {
      const balance = await this.connection.getBalance(new PublicKey(walletPublicKey));
      console.log('Wallet balance:', (balance / LAMPORTS_PER_SOL).toFixed(4), 'SOL');
      if (balance < 0.0001 * LAMPORTS_PER_SOL) {
        throw new Error(`Insufficient balance: ${balance / LAMPORTS_PER_SOL} SOL`);
      }
    } catch (e) {
      console.warn('Could not check balance:', e);
    }

    // Prepare transaction
    if (!transaction.recentBlockhash) {
      console.log('Getting fresh blockhash...');
      const { blockhash } = await this.connection.getLatestBlockhash('finalized');
      transaction.recentBlockhash = blockhash;
      console.log('Blockhash:', blockhash.substring(0, 10) + '...');
    }

    if (!transaction.feePayer) {
      transaction.feePayer = new PublicKey(walletPublicKey);
    }

    // IMPORTANT: serialize the Transaction (never a Message)
    const serializedTransaction = transaction.serialize({
      requireAllSignatures: false,
      verifySignatures: false
    });

    console.log('Serialized size:', serializedTransaction.length, 'bytes');

    // Encrypt payload
    const payload = {
      transaction: bs58.encode(serializedTransaction),
      session
    };

    const nonce = nacl.randomBytes(24);
    const payloadBytes = new TextEncoder().encode(JSON.stringify(payload));
    const encrypted = nacl.box.after(payloadBytes, nonce, this.sharedSecret!);

    const redirectLink = `${window.location.origin}/phantom-signing-redirect`;

    const params = new URLSearchParams({
      dapp_encryption_public_key: dappPublicKey,
      nonce: bs58.encode(nonce),
      redirect_link: redirectLink,
      payload: bs58.encode(encrypted)
    });

    // Prefer universal link; fallback to custom scheme shortly after
    const ul = `https://phantom.app/ul/v1/signTransaction?${params.toString()}`;
    const custom = `phantom://v1/signTransaction?${params.toString()}`;

    console.log('URL length:', ul.length);
    if (ul.length > 2000) {
      console.warn('⚠️ URL very long, might fail on some mobile browsers.');
    }

    // Store pending transaction (for your post-redirect UX)
    localStorage.setItem('phantom_pending_transaction', JSON.stringify({
      transaction: bs58.encode(serializedTransaction),
      timestamp: Date.now(),
      type: 'sign_transaction'
    }));

    console.log('🚀 Opening Phantom app with deeplink...');
    // Try UL first, fallback to custom scheme
    window.location.href = ul;
    setTimeout(() => {
      try { window.location.href = custom; } catch {}
    }, 350);

    // Stop the current flow; caller should expect this throw on mobile
    return new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Redirected')), 30000);
    });

  } catch (error) {
    console.error('Signing failed:', error);
    localStorage.removeItem('phantom_pending_transaction');
    throw error;
  }
}

  async signAndSendTransaction(transaction: Transaction): Promise<string> {
  if (!this.hasValidSession()) {
    throw new Error('No valid session. Please connect first.');
  }

  if (!this.sharedSecret && !this.restoreSharedSecret()) {
    throw new Error('Session expired. Please reconnect.');
  }

  try {
    const walletPublicKey = localStorage.getItem('phantom_public_key');
    const session = localStorage.getItem('phantom_session');
    const dappPublicKey = localStorage.getItem('phantom_dapp_public_key');

    if (!walletPublicKey || !session || !dappPublicKey) {
      throw new Error('Missing session data');
    }

    if (!transaction.recentBlockhash) {
      const { blockhash } = await this.connection.getLatestBlockhash('finalized');
      transaction.recentBlockhash = blockhash;
    }

    if (!transaction.feePayer) {
      transaction.feePayer = new PublicKey(walletPublicKey);
    }

    const serializedTransaction = transaction.serialize({
      requireAllSignatures: false,
      verifySignatures: false
    });

    const payload = {
      transaction: bs58.encode(serializedTransaction),
      session,
      sendOptions: {
        skipPreflight: false,
        preflightCommitment: 'confirmed'
      }
    };

    const nonce = nacl.randomBytes(24);
    const payloadBytes = new TextEncoder().encode(JSON.stringify(payload));
    const encrypted = nacl.box.after(payloadBytes, nonce, this.sharedSecret!);

    const params = new URLSearchParams({
      dapp_encryption_public_key: dappPublicKey,
      nonce: bs58.encode(nonce),
      redirect_link: `${window.location.origin}/phantom-signing-redirect`,
      payload: bs58.encode(encrypted)
    });

    const ul = `https://phantom.app/ul/v1/signAndSendTransaction?${params.toString()}`;
    const custom = `phantom://v1/signAndSendTransaction?${params.toString()}`;

    localStorage.setItem('phantom_pending_transaction', JSON.stringify({
      transaction: bs58.encode(serializedTransaction),
      timestamp: Date.now(),
      type: 'sign_and_send_transaction'
    }));

    console.log('🚀 Opening Phantom app for sign and send...');
    window.location.href = ul;
    setTimeout(() => {
      try { window.location.href = custom; } catch {}
    }, 350);

    return new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Redirected')), 30000);
    });
  } catch (error) {
    console.error('SignAndSend failed:', error);
    throw error;
  }
}

  async handleSigningRedirect(): Promise<any> {
  const urlParams = new URLSearchParams(window.location.search);

  // Check for errors first
  const errorMessage = urlParams.get('errorMessage');
  const errorCode = urlParams.get('errorCode');

  if (errorMessage || errorCode) {
    const pending = localStorage.getItem('phantom_pending_transaction');
    const pendingObj = pending ? JSON.parse(pending) : null;

    console.error('Phantom error:', { code: errorCode, message: errorMessage });

    // Fallback: method not supported → retry via signTransaction deeplink
    if (errorCode === '-32601' && pendingObj?.type === 'sign_and_send_transaction') {
      try {
        // We need session, dapp pubkey, shared secret, and the same tx
        const session = localStorage.getItem('phantom_session');
        const dappPublicKey = localStorage.getItem('phantom_dapp_public_key');
        if (!this.sharedSecret && !this.restoreSharedSecret()) {
          throw new Error('Session expired - no shared secret available');
        }
        if (!session || !dappPublicKey || !pendingObj?.transaction) {
          throw new Error('Missing session or transaction for fallback');
        }

        // Build signTransaction payload (same tx)
        const payload = {
          transaction: pendingObj.transaction, // base58
          session
        };
        const nonce = nacl.randomBytes(24);
        const payloadBytes = new TextEncoder().encode(JSON.stringify(payload));
        const encrypted = nacl.box.after(payloadBytes, nonce, this.sharedSecret!);

        const params = new URLSearchParams({
          dapp_encryption_public_key: dappPublicKey,
          nonce: bs58.encode(nonce),
          redirect_link: `${window.location.origin}/phantom-signing-redirect`,
          payload: bs58.encode(encrypted)
        });

        const ul = `https://phantom.app/ul/v1/signTransaction?${params.toString()}`;
        const custom = `phantom://v1/signTransaction?${params.toString()}`;

        // Update pending to reflect new flow
        localStorage.setItem('phantom_pending_transaction', JSON.stringify({
          transaction: pendingObj.transaction,
          timestamp: Date.now(),
          type: 'sign_transaction'
        }));

        console.log('⚙️ Fallback to signTransaction deeplink…');
        window.location.href = ul;
        setTimeout(() => { try { window.location.href = custom; } catch {} }, 350);

        throw new Error('Redirecting (fallback to signTransaction)');
      } catch (e) {
        localStorage.removeItem('phantom_pending_transaction');
        throw e;
      }
    }

    // Your existing mapping
    localStorage.removeItem('phantom_pending_transaction');
    if (errorCode === '-32603') {
      throw new Error('Transaction rejected or invalid');
    }
    if (errorCode === '-32000') {
      throw new Error('User cancelled transaction');
    }
    throw new Error(`Phantom error: ${errorMessage || 'Unknown error'}`);
  }

  // Get the parameters
  const phantomEncryptionPublicKey = urlParams.get('phantom_encryption_public_key');
  const data = urlParams.get('data');
  const nonce = urlParams.get('nonce');

  // Determine if this is a connection response or a signing response
  const isConnectionResponse = !!(phantomEncryptionPublicKey && data && nonce);
  const isSigningResponse = !!(data && nonce && !phantomEncryptionPublicKey);

  if (!isConnectionResponse && !isSigningResponse) {
    localStorage.removeItem('phantom_pending_transaction');
    throw new Error('Invalid redirect parameters - missing required fields');
  }

  try {
    // Handle connection response (unchanged)
    if (isConnectionResponse) {
      if (!this.encryptionKeys) throw new Error('Encryption keys not initialized');
      const phantomPubKeyBytes = bs58.decode(phantomEncryptionPublicKey!);
      this.sharedSecret = nacl.box.before(phantomPubKeyBytes, this.encryptionKeys.secretKey);

      const connectionData = this.decryptPayload(data!, nonce!, this.sharedSecret);
      const publicKey = connectionData.public_key || connectionData.publicKey;
      if (!publicKey) throw new Error('No public key in connection data');

      this.session = connectionData.session;
      if (this.session) localStorage.setItem('phantom_session', this.session);

      localStorage.setItem('phantom_shared_secret', JSON.stringify(Array.from(this.sharedSecret)));
      localStorage.setItem('phantom_public_key', publicKey);
      localStorage.setItem('phantom_connected_at', Date.now().toString());
      localStorage.setItem('phantom_encryption_public_key', phantomEncryptionPublicKey!);
      localStorage.removeItem('phantom_connection_attempt');
      localStorage.removeItem('phantom_expected_origin');

      console.log('Phantom connection successful:', publicKey);
      return publicKey;
    }

    // Handle signing response (with auto-send if needed)
    if (isSigningResponse) {
      if (!this.sharedSecret && !this.restoreSharedSecret()) {
        throw new Error('Session expired - no shared secret available');
      }

      const decrypted = nacl.box.open.after(
        bs58.decode(data!),
        bs58.decode(nonce!),
        this.sharedSecret!
      );
      if (!decrypted) throw new Error('Failed to decrypt signing response');

      const response = JSON.parse(new TextDecoder().decode(decrypted));
      console.log('Signing response received:', Object.keys(response));

      const pending = localStorage.getItem('phantom_pending_transaction');
      const pendingObj = pending ? JSON.parse(pending) : null;
      localStorage.removeItem('phantom_pending_transaction');

      // 1) If Phantom already sent us a network signature → return it
      if (response.signature) {
        console.log('Transaction signature received');
        return response.signature;
      }

      // 2) If we got a signed transaction (signTransaction flow), send it now
      if (response.transaction) {
        console.log('Signed transaction received; submitting to RPC…');
        const raw = bs58.decode(response.transaction);
        const sig = await this.connection.sendRawTransaction(raw, {
          skipPreflight: false,
          preflightCommitment: 'confirmed'
        });
        await this.connection.confirmTransaction(sig, 'confirmed');
        return sig;
      }

      // 3) Multiple signed transactions not used here, but support if needed
      if (response.transactions && Array.isArray(response.transactions)) {
        // Submit first for convenience; you can expand this if you actually batch on desktop
        const raw = bs58.decode(response.transactions[0]);
        const sig = await this.connection.sendRawTransaction(raw, {
          skipPreflight: false,
          preflightCommitment: 'confirmed'
        });
        await this.connection.confirmTransaction(sig, 'confirmed');
        return sig;
      }

      console.error('Unexpected response format:', response);
      throw new Error('No signature in response');
    }
  } catch (error) {
    console.error('Failed to handle redirect:', error);
    localStorage.removeItem('phantom_pending_transaction');
    throw error;
  }
}



  async createTestTransaction(): Promise<Transaction> {
    const walletPublicKey = this.getStoredPublicKey();
    if (!walletPublicKey) {
      throw new Error('No wallet connected');
    }

    const publicKey = new PublicKey(walletPublicKey);
    
    // Check balance
    const balance = await this.connection.getBalance(publicKey);
    console.log('Test transaction for wallet:', walletPublicKey);
    console.log('Balance:', (balance / LAMPORTS_PER_SOL).toFixed(4), 'SOL');
    
    if (balance < 0.0001 * LAMPORTS_PER_SOL) {
      throw new Error('Insufficient balance for test transaction');
    }

    const transaction = new Transaction();
    
    const { blockhash } = await this.connection.getLatestBlockhash('finalized');
    transaction.recentBlockhash = blockhash;
    transaction.feePayer = publicKey;
    
    // Minimal self-transfer (0 SOL)
    transaction.add(
      SystemProgram.transfer({
        fromPubkey: publicKey,
        toPubkey: publicKey,
        lamports: 0
      })
    );

    return transaction;
  }

  private decryptPayload(data: string, nonce: string, sharedSecret: Uint8Array): any {
    try {
      const decrypted = nacl.box.open.after(
        bs58.decode(data),
        bs58.decode(nonce),
        sharedSecret
      );

      if (!decrypted) {
        throw new Error('Decryption failed');
      }

      return JSON.parse(new TextDecoder().decode(decrypted));
    } catch (error) {
      console.error('Decryption error:', error);
      throw error;
    }
  }

  hasValidSession(): boolean {
    const session = localStorage.getItem('phantom_session');
    const publicKey = localStorage.getItem('phantom_public_key');
    const connectedAt = localStorage.getItem('phantom_connected_at');
    
    if (connectedAt) {
      const age = Date.now() - parseInt(connectedAt);
      const maxAge = 24 * 60 * 60 * 1000; // 24 hours
      if (age > maxAge) {
        console.log('Session expired');
        this.clearSession();
        return false;
      }
    }
    
    return !!(session && publicKey);
  }

  getStoredPublicKey(): string | null {
    if (this.hasValidSession()) {
      return localStorage.getItem('phantom_public_key');
    }
    return null;
  }

  clearSession(): void {
    const keysToRemove = [
      'phantom_session',
      'phantom_shared_secret',
      'phantom_public_key',
      'phantom_connection_attempt',
      'phantom_expected_origin',
      'phantom_connected_at',
      'phantom_encryption_public_key',
      'phantom_dapp_public_key',
      'phantom_pending_transaction',
      'phantom_network',
      'dapp_encryption_keypair'
    ];

    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    this.session = null;
    this.sharedSecret = null;
    this.encryptionKeys = null;
    console.log('Session cleared');
  }

  isInRedirectFlow(): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('phantom_redirect') === 'true' || 
           (urlParams.has('phantom_encryption_public_key') && 
            urlParams.has('data') && 
            urlParams.has('nonce'));
  }

  isConnectionTimedOut(): boolean {
    const attempt = localStorage.getItem('phantom_connection_attempt');
    if (!attempt) return false;
    
    const age = Date.now() - parseInt(attempt);
    return age > (5 * 60 * 1000); // 5 minutes
  }

  isExpectingRedirect(): boolean {
    const attempt = localStorage.getItem('phantom_connection_attempt');
    return !!attempt && !this.isConnectionTimedOut();
  }

  async signAllTransactions(transactions: Transaction[]): Promise<Transaction[]> {
    // Implementation same as before but with network consistency
    throw new Error('Not implemented - use signTransaction for now');
  }

  async signMessage(message: string): Promise<string> {
    // Implementation same as before but with network consistency
    throw new Error('Not implemented - use signTransaction for now');
  }
}

const phantomDeeplinkService = new PhantomDeeplinkService();
export default phantomDeeplinkService;